import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { getCartAccessToken, useCart } from "../lib/cart";
import { useAuth } from "../lib/auth";
import { formatAZN, pickLocale } from "../lib/format";
import { PillCTA } from "../components/atelier/primitives";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { productImage } from "../components/storefront/assets";
import AdfImage from "../components/storefront/AdfImage";
import { useI18n } from "../lib/i18n";
import { useStoreLocations } from "../hooks/useSiteContent";
import { AZ_PREFIX, e164Phone, formatLocalPhone, isCompletePhone, localPhoneDigits } from "../lib/phone";
import { apiErrorMessage } from "../lib/apiErrors";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { analytics, toItem } from "../lib/analytics";

/* Two working steps; the third circle is the success page itself. The old
   layout split contact from address into separate screens, and the owner's
   reference collapses them: everything about getting the order to a person is
   one step, reviewing and paying is the next. */
const STEP_KEYS = [
  ["checkout.step.delivery", "Çatdırılma"],
  ["checkout.step.review_pay", "Nəzərdən keç və Ödə"],
  ["checkout.step.done", "Tamamla"],
];
const FALLBACK_PROVIDER_CODES = ["cod", "bank_transfer"];
/* Which delivery codes mean "the shopper walks in". Everything else is a
   courier and needs an address. */
const PICKUP_CODES = ["store", "pickup", "atelier"];

function isEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

export default function Checkout() {
  const { cart, complete, adoptCart, setDeliveryMethod } = useCart();
  const { user, requestOtp, verifyOtp } = useAuth();
  const { t, locale } = useI18n();
  useDocumentTitle(t("checkout.doc_title", "Ödəniş"));
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  /* Falcon Points spent on this order. The balance is fetched, never trusted:
     the server validates the amount again when the order is placed. */
  const [loyalty, setLoyalty] = useState(null);
  const [redeem, setRedeem] = useState(0);
  const [providers, setProviders] = useState([]);
  const [busy, setBusy] = useState(false);
  // Placing the order empties the basket server-side; while the browser is on
  // its way to the payment gateway we must not flash the "basket is empty"
  // page. This stays true from a successful place-order until the redirect.
  const [redirecting, setRedirecting] = useState(false);
  const [err, setErr] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [deliveryMethods, setDeliveryMethods] = useState([]);

  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpVerified, setOtpVerified] = useState(Boolean(user));
  const [otpDebugCode, setOtpDebugCode] = useState("");
  /* The number the code was actually stored against, as the server normalised
     it. Verification matches on that exact string. */
  const [otpPhone, setOtpPhone] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpInfo, setOtpInfo] = useState("");
  const [resendSeconds, setResendSeconds] = useState(0);
  const [form, setForm] = useState({
    email: user?.email || "",
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    phone: user?.phone || "",
    country: "AZ",
    city: "Bakı",
    line1: "",
    line2: "",
    postal_code: "",
    delivery: "",
    payment_provider: "cod",
    notes: "",
    marketing_consent: false,
    terms_accepted: false,
  });
  /* The order as a present: the courier calls the person receiving it, not the
     person paying for it. The buyer's own identity stays on the order. */
  const [giftEnabled, setGiftEnabled] = useState(false);
  const [gift, setGift] = useState({ first_name: "", last_name: "", phone: "", note: "" });
  /* Which boutique hands the order over, when collecting in store. */
  const [pickupStoreId, setPickupStoreId] = useState("");

  useEffect(() => {
    api.get("/payment-providers")
      .then((r) => setProviders(r.data || []))
      .catch((err) => console.error("[atelier] payment providers fetch failed", err));
  }, []);

  /* Signed-out shoppers have no balance and the request 401s, so it is only
     asked for when there is someone to ask about. */
  useEffect(() => {
    if (!user) {
      setLoyalty(null);
      return;
    }
    api.get("/me/loyalty")
      .then((r) => setLoyalty(r.data))
      .catch(() => setLoyalty(null));
  }, [user]);

  useEffect(() => {
    if (user) {
      setForm(f => ({
        ...f,
        email: user.email || f.email,
        first_name: user.first_name || f.first_name,
        last_name: user.last_name || f.last_name,
        phone: user.phone || f.phone,
      }));
      setOtpVerified(true);
    }
  }, [user]);

  useEffect(() => {
    if (resendSeconds <= 0) return undefined;
    const timer = setInterval(() => {
      setResendSeconds((current) => Math.max(0, current - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendSeconds]);

  const fallbackProviders = FALLBACK_PROVIDER_CODES.map((code) => ({
    code,
    display_name: t(`checkout.provider.${code}`, code === "cod" ? "Nağd ödəniş" : "Bank köçürməsi"),
  }));
  const availableProviders = (providers.length ? providers : fallbackProviders)
    .filter(p => ["cod", "bank_transfer", "stripe", "kapitalbank"].includes(p.code))
    /* The API's display_name is unlocalised ("Cash on Delivery") — on an
       Azerbaijani page the method's name is this page's to translate. */
    .map(p => ({ ...p, display_name: t(`checkout.provider.${p.code}`, p.display_name || p.code) }));

  /* The methods the admin panel configured, split into the two tabs of the
     reference design: couriers on one side, walk-in collection on the other. */
  const methodOptions = useMemo(() => (deliveryMethods || []).map((method) => {
    const price = Number(method.price || 0);
    const eta = [method.eta_min_days, method.eta_max_days].filter((n) => n != null);
    return {
      code: method.code,
      pickup: PICKUP_CODES.includes(method.code),
      name: pickLocale(method.name, locale) || method.code,
      description: pickLocale(method.description, locale),
      price,
      fee: price > 0 ? formatAZN(price, locale) : t("checkout.delivery.free", "Ödənişsiz"),
      eta: eta.length ? `${eta.join("–")} ${t("checkout.delivery.days", "gün")}` : "",
    };
  }), [deliveryMethods, locale, t]);
  const courierOptions = methodOptions.filter((o) => !o.pickup);
  const pickupOptions = methodOptions.filter((o) => o.pickup);
  const selectedDelivery = methodOptions.find((option) => option.code === form.delivery);
  /* Which tab is open follows the chosen method, so the state cannot disagree
     with the order that will be placed. */
  const deliveryMode = selectedDelivery?.pickup ? "pickup" : "courier";
  const needsAddress = deliveryMode === "courier";

  /* OTP verification swaps the guest cart for the account's cart, and the
     adopted cart is priced for the server default — the radio kept saying
     "Bakı · 10 ₼" while the summary and the confirm button quietly dropped
     the fee, so a COD shopper confirmed 10 ₼ less than the order recorded.
     Whenever the cart identity changes, the chosen method is re-applied. */
  const cartId = cart?.id;
  useEffect(() => {
    if (cartId && form.delivery) setDeliveryMethod(form.delivery).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId]);

  const allStores = useStoreLocations() || [];
  /* Outlet pieces are stocked only at the outlet boutique — a basket holding
     one narrows the pickup choice to it. The server enforces the same rule. */
  const hasOutletItems = (cart?.items || []).some((it) => it.outlet);
  const pickupStores = hasOutletItems ? allStores.filter((s) => s.is_outlet) : allStores;
  const pickupStore = pickupStores.find((s) => s.id === pickupStoreId) || null;
  useEffect(() => {
    if (pickupStores.length === 1) setPickupStoreId(pickupStores[0].id);
    else if (pickupStoreId && !pickupStores.some((s) => s.id === pickupStoreId)) setPickupStoreId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOutletItems, allStores.length]);

  const chooseDelivery = (code) => {
    setForm((current) => ({ ...current, delivery: code }));
    /* The fee is the server's to calculate — it knows the free-over
       threshold. The summary updates from the cart it returns. */
    setDeliveryMethod(code).catch(() => {});
  };

  const validateStep = (targetStep = step) => {
    const next = {};
    if (targetStep === 0) {
      if (!form.delivery) next.delivery = t("checkout.error.delivery_required", "Çatdırılma üsulunu seçin.");
      if (!isCompletePhone(form.phone)) next.phone = t("checkout.error.phone_required", "Telefon nömrəsi tələb olunur.");
      if (!otpVerified) next.otp = t("checkout.error.otp_required", "Telefon nömrənizi təsdiqləyin.");
      if (!form.first_name.trim()) next.first_name = t("checkout.error.first_required", "Ad tələb olunur.");
      if (!form.last_name.trim()) next.last_name = t("checkout.error.last_required", "Soyad tələb olunur.");
      if (form.email.trim() && !isEmail(form.email)) next.email = t("checkout.error.email_invalid", "Düzgün e-poçt ünvanı yazın.");
      if (needsAddress) {
        if (!form.city.trim()) next.city = t("checkout.error.city_required", "Şəhər tələb olunur.");
        if (!form.line1.trim()) next.line1 = t("checkout.error.address_required", "Ünvan tələb olunur.");
      } else if (pickupStores.length > 0 && !pickupStoreId) {
        next.pickup_store = t("checkout.error.store_required", "Mağazanı seçin.");
      }
      if (giftEnabled) {
        if (!gift.first_name.trim()) next.gift_first = t("checkout.error.gift_first_required", "Qəbul edən şəxsin adı tələb olunur.");
        if (!isCompletePhone(gift.phone)) next.gift_phone = t("checkout.error.gift_phone_required", "Qəbul edən şəxsin mobil nömrəsi tələb olunur.");
      }
      if (!form.terms_accepted) next.terms_accepted = t("checkout.error.terms_required", "Tələb olunur.");
    }
    if (targetStep === 1 && !form.payment_provider) next.payment_provider = t("checkout.error.payment_required", "Ödəniş üsulunu seçin.");
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const continueNext = () => {
    setErr("");
    if (!validateStep(step)) return;
    setStep(step + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    let active = true;
    api.get("/delivery-methods")
      .then((r) => {
        if (!active) return;
        const methods = (r.data || []).filter((m) => m.active !== false);
        setDeliveryMethods(methods);
        /* The default is chosen visibly — a courier first if one exists, so
           the address form matches what most orders need. */
        setForm((current) => {
          if (methods.length && !methods.some((m) => m.code === current.delivery)) {
            const courier = methods.find((m) => !PICKUP_CODES.includes(m.code));
            const code = (courier || methods[0]).code;
            /* The cart arrives priced for the server's default method; if the
               visible default differs, reprice now — or the radio names one
               fee while the summary charges another. */
            setDeliveryMethod(code).catch(() => {});
            return { ...current, delivery: code };
          }
          return current;
        });
      })
      .catch(() => {});
    return () => { active = false; };
    /* Run once on mount: setDeliveryMethod is stable for the life of the
       cart hook, and re-running this on its identity would re-fetch the
       method list for nothing. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendOtp = async () => {
    setErr(""); setOtpInfo(""); setOtpDebugCode("");
    const phone = e164Phone(form.phone);
    if (!phone) {
      setFieldErrors({ phone: t("checkout.error.phone_invalid", "9 rəqəmli mobil nömrəni tam yazın.") });
      return;
    }
    setOtpBusy(true);
    try {
      const res = await requestOtp(phone, "checkout");
      setOtpSent(true);
      setOtpPhone(res.phone || phone);
      setOtpInfo(t("checkout.otp.code_sent", "Kod +{phone} nömrəsinə göndərildi.", { phone: res.phone }));
      setResendSeconds(res.resend_after_seconds || 60);
      if (res.debug_code) setOtpDebugCode(res.debug_code);
    } catch (ex) {
      setErr(apiErrorMessage(ex, t));
    } finally {
      setOtpBusy(false);
    }
  };

  const confirmOtp = async () => {
    setErr(""); setOtpInfo("");
    if (!form.terms_accepted) {
      /* Əvvəl xəta yalnız xeyli aşağıdakı checkbox-da çıxırdı — istifadəçi
         kodu yazıb «Təsdiqlə» basır, heç nə baş vermir kimi görünürdü
         («birincidən təsdiqlənmir» şikayəti). Səbəb kod xanasının YANINDA
         deyilir. */
      setFieldErrors({
        otp: t("checkout.error.terms_first", "Davam üçün aşağıda şərtləri qəbul edin."),
        terms_accepted: t("checkout.error.terms_required", "Tələb olunur."),
      });
      return;
    }
    if (!otpCode.trim()) {
      setFieldErrors({ otp: t("checkout.error.code_required", "Kodu daxil edin.") });
      return;
    }
    setOtpBusy(true);
    try {
      const res = await verifyOtp({
        /* Not `form.phone`: that is whatever is in the box, and the server only
           accepts E.164. The number the code was sent to is the one that can
           confirm it. */
        phone: otpPhone || e164Phone(form.phone),
        code: otpCode.trim(),
        purpose: "checkout",
        terms_accepted: form.terms_accepted,
        email: form.email || null,
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        marketing_consent: form.marketing_consent,
        cart_id: cart?.id,
        cart_access_token: getCartAccessToken(),
      });
      if (res.cart_id && adoptCart) await adoptCart(res.cart_id);
      setOtpVerified(true);
      setOtpInfo(t("checkout.otp.phone_verified", "Telefon təsdiqləndi."));
      setForm(f => ({
        ...f,
        email: res.user?.email || f.email,
        first_name: res.user?.first_name || f.first_name,
        last_name: res.user?.last_name || f.last_name,
        phone: res.user?.phone || f.phone,
      }));
    } catch (ex) {
      setErr(apiErrorMessage(ex, t));
    } finally {
      setOtpBusy(false);
    }
  };

  /* Kod tam (6 rəqəm) yazılan kimi avto-təsdiq — «Təsdiqlə»ni basmağı
     unutmaq/ikinci cəhd problemi qalmasın. Eyni kod üçün yalnız bir cəhd
     (ref); uğursuzsa istifadəçi kodu dəyişəndə yenidən işə düşür. */
  const otpAutoTried = useRef("");
  useEffect(() => {
    const code = otpCode.trim();
    if (!otpSent || otpVerified || otpBusy) return;
    if (code.length !== 6 || !/^\d{6}$/.test(code)) return;
    const attempt = `${code}:${form.terms_accepted ? 1 : 0}`;
    if (otpAutoTried.current === attempt) return;
    otpAutoTried.current = attempt;
    void confirmOtp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpCode, otpSent, otpVerified, otpBusy, form.terms_accepted]);

  if (!cart || (cart.items || []).length === 0) {
    // Mid-checkout (submitting or heading to the gateway) the basket is emptied
    // on purpose — show a quiet "redirecting to payment" state, not the empty
    // basket page, so nothing flashes on the way to Kapital Bank.
    if (busy || redirecting) {
      return (
        <div className="min-h-screen bg-white text-graphite" data-testid="checkout-redirecting">
          <Header />
          <main id="main" className="adf-container">
            <section className="mx-auto flex max-w-md flex-col items-center px-6 py-32 text-center">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-graphite/20 border-t-graphite" aria-hidden="true" />
              <p className="mt-6 font-light text-graphite/70">
                {t("checkout.redirecting", "Ödəniş səhifəsinə yönləndirilir…")}
              </p>
            </section>
          </main>
          <Footer />
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-white text-graphite" data-testid="checkout-empty">
        <Header />
        <main id="main" className="adf-container">
          <section className="mx-auto flex max-w-md flex-col items-center px-6 py-20 text-center">
            <h1 className="font-serif text-3xl text-graphite/80">
              {t("checkout.empty.title", "Səbətiniz boşdur.")}
            </h1>
            <p className="mt-3 font-light text-graphite/70">
              {t("checkout.empty.body", "Ödənişə keçmək üçün əvvəlcə səbətinizə məhsul əlavə edin.")}
            </p>
            <PillCTA as={Link} to="/shop" variant="primary" className="mt-8" data-testid="checkout-empty-cta">
              {t("checkout.empty.cta", "Kolleksiyalara bax")}
            </PillCTA>
            <Link to="/cart" className="mt-5 min-h-11 text-sm text-graphite/70 underline underline-offset-4 hover:text-graphite">
              {t("checkout.empty.cart", "Səbətə bax")}
            </Link>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  const submit = async () => {
    for (const idx of [0, 1]) {
      if (!validateStep(idx)) {
        setStep(idx);
        return;
      }
    }
    setErr(""); setBusy(true);
    try {
      /* A gift ships to the recipient: their name and phone go on the
         address, because that is who the courier will call. The buyer stays
         on the order itself (first_name / phone / email below). */
      const contactFirst = giftEnabled && gift.first_name.trim() ? gift.first_name : form.first_name;
      const contactLast = giftEnabled && gift.first_name.trim() ? gift.last_name : form.last_name;
      const contactPhone = giftEnabled && isCompletePhone(gift.phone) ? gift.phone : form.phone;
      const pickupLine = pickupStore
        ? [pickLocale(pickupStore.title, locale), pickLocale(pickupStore.address, locale)].filter(Boolean).join(" — ")
        : t("checkout.pickup.address_line", "Mağazadan götürmə");
      const address = {
        first_name: contactFirst,
        last_name: contactLast,
        phone: contactPhone,
        country: form.country,
        city: needsAddress ? form.city : "Bakı",
        line1: needsAddress ? form.line1 : pickupLine,
        line2: needsAddress ? form.line2 : "",
        postal_code: needsAddress ? form.postal_code : "",
      };
      const noteParts = [form.notes.trim()];
      if (giftEnabled) {
        noteParts.push(t("checkout.gift.note_tag", "Hədiyyə sifarişi") + ` — ${contactFirst} ${contactLast}, ${contactPhone}`.trim());
        if (gift.note.trim()) noteParts.push(`${t("checkout.gift.note_label", "Qeyd")}: ${gift.note.trim()}`);
      }
      analytics.addPaymentInfo(
        (cart?.items || []).map((line) => toItem(line.product || line, {
          quantity: line.quantity,
          item_variant: line.variant_title,
        })).filter(Boolean),
        cart?.total,
        form.payment_provider,
      );
      const res = await complete({
        payment_provider: form.payment_provider,
        email: form.email || null,
        shipping_address: address,
        billing_address: address,
        notes: noteParts.filter(Boolean).join("\n") || null,
        delivery_method: form.delivery || null,
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone,
        terms_accepted: form.terms_accepted,
        marketing_consent: form.marketing_consent,
        create_account: false,
        /* The server validates this again — balance, multiple and the
           half-order ceiling — so a hand-made request buys nothing extra. */
        redeem_points: redeemApplied,
        pickup_store_id: needsAddress ? null : (pickupStoreId || null),
      });
      // The order exists and the basket is now empty; every branch below leaves
      // this page. Hold the redirecting state so the emptied basket never shows.
      setRedirecting(true);
      analytics.purchase(res.order);
      /* A gateway that answered with a redirect gets it followed as-is —
         Kapital Bank returns its hosted payment page URL this way. */
      if (res.payment_action?.type === "redirect" && res.payment_action.url) {
        window.location.href = res.payment_action.url;
        return;
      }
      /* The gateway refused to even start (bad credentials, wrong environment,
         or not configured). The order is placed but unpaid and the basket is
         already emptied, so we can't stay on this page — send the shopper to
         the paused/cancelled result with a reason and a retry, the same place a
         gateway decline lands them, instead of a false success page. */
      if (res.payment_action?.type === "error" || res.payment_action?.type === "not_configured") {
        navigate(`/checkout/cancelled?order_id=${res.order.id}&reason=unavailable&provider=${form.payment_provider}`);
        return;
      }
      if (form.payment_provider === "stripe") {
        try {
          const r = await api.post("/payments/stripe/checkout", { order_id: res.order.id });
          window.location.href = r.data.url;
        } catch {
          navigate(`/checkout/cancelled?order_id=${res.order.id}`);
        }
        return;
      }
      if (user || otpVerified) navigate(`/orders/${res.order.id}?just_placed=1`);
      else {
        const contactQuery = form.email
          ? `email=${encodeURIComponent(form.email)}`
          : `phone=${encodeURIComponent(form.phone)}`;
        navigate(`/checkout/success?order_id=${res.order.id}&${contactQuery}`,
          { state: { account: res.account || null } });
      }
    } catch (ex) {
      setErr(apiErrorMessage(ex, t));
    } finally { setBusy(false); }
  };

  /* 20 points buy 1 AZN — the same rate the account-area voucher uses.
     Mirrored from the server's VOUCHER_POINTS_PER_AZN. */
  const POINTS_PER_AZN = 20;
  const redeemValue = (points) => Math.floor(points / POINTS_PER_AZN) * 100;
  const goodsTotal = Math.max(0, (cart?.total || 0) - (cart?.shipping_total || 0));
  const redeemMax = (() => {
    const balance = loyalty?.balance || 0;
    if (!balance || !goodsTotal) return 0;
    const byBalance = Math.floor(balance / POINTS_PER_AZN) * POINTS_PER_AZN;
    const byOrder = Math.floor(goodsTotal * 0.5 / 100) * POINTS_PER_AZN;
    return Math.max(0, Math.min(byBalance, byOrder));
  })();
  const redeemApplied = Math.min(redeem, redeemMax);
  const payableTotal = Math.max(0, (cart?.total || 0) - redeemValue(redeemApplied));

  return (
    <div className="min-h-screen bg-white text-graphite" data-testid="checkout-page">
      <Header />
      <main id="main" className="mx-auto max-w-[1240px] px-5 pb-20 pt-10 sm:px-8 lg:px-10">
        <h1 className="text-center font-serif text-[26px] leading-none sm:text-[30px]">
          {t("checkout.title", "Sifarişi tamamla")}
        </h1>

        {/* The reference stepper: numbered circles joined by hairlines, the
            label under each. The third circle belongs to the success page. */}
        <div className="mx-auto mt-8 flex max-w-[640px] items-start" data-testid="checkout-stepper">
          {STEP_KEYS.map(([key, fallback], i) => (
            <React.Fragment key={key}>
              {i > 0 && <div className={`mt-[19px] h-px flex-1 ${i <= step ? "bg-graphite" : "bg-graphite/20"}`} aria-hidden="true" />}
              <div className="flex w-[110px] flex-col items-center sm:w-[150px]" data-testid={`step-${i}`}>
                <span className={`flex h-10 w-10 items-center justify-center rounded-full border text-sm ${i === step ? "border-graphite font-medium text-graphite" : i < step ? "border-graphite bg-graphite text-white" : "border-graphite/25 text-graphite/50"}`}>
                  {i + 1}
                </span>
                <span className={`mt-2 text-center text-[13px] leading-tight sm:text-sm ${i === step ? "text-graphite" : "text-graphite/55"}`}>{t(key, fallback)}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        <section className="mt-12 grid gap-10 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
        <div className="order-2 lg:order-1">
          {step === 0 && (
            <div data-testid="checkout-delivery">
              <h2 className="font-serif text-xl">{t("checkout.delivery.pick_type", "Çatdırılma növünü seçin")}</h2>

              {/* Courier or walk-in — the two tabs of the reference. */}
              <div className="mt-5 flex flex-wrap gap-3" data-testid="delivery-mode">
                <button
                  type="button"
                  onClick={() => courierOptions[0] && chooseDelivery(courierOptions[0].code)}
                  disabled={!courierOptions.length}
                  className={`min-h-[48px] border px-6 text-[15px] transition ${deliveryMode === "courier" ? "border-graphite bg-graphite text-white" : "border-graphite/30 bg-white text-graphite hover:border-graphite"} disabled:opacity-40`}
                  data-testid="delivery-mode-courier"
                >
                  {t("checkout.delivery.courier", "Çatdırılma")}
                </button>
                <button
                  type="button"
                  onClick={() => pickupOptions[0] && chooseDelivery(pickupOptions[0].code)}
                  disabled={!pickupOptions.length}
                  className={`min-h-[48px] border px-6 text-[15px] transition ${deliveryMode === "pickup" ? "border-graphite bg-graphite text-white" : "border-graphite/30 bg-white text-graphite hover:border-graphite"} disabled:opacity-40`}
                  data-testid="delivery-mode-pickup"
                >
                  {t("checkout.delivery.pickup_tab", "Mağazadan götürmək")}
                </button>
              </div>

              <div className="mt-6 space-y-1">
                {(deliveryMode === "courier" ? courierOptions : pickupOptions).map((o) => (
                  <label key={o.code} data-testid={`delivery-${o.code}`} className="flex min-h-[52px] cursor-pointer items-center justify-between gap-4 py-2">
                    <span className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="delivery"
                        checked={form.delivery === o.code}
                        onChange={() => chooseDelivery(o.code)}
                        className="h-4 w-4 accent-graphite"
                      />
                      <span className="text-[13px] uppercase tracking-[0.12em]">
                        {o.name}
                        {o.eta && <span className="ml-3 normal-case tracking-normal text-graphite/55">{o.eta}</span>}
                      </span>
                    </span>
                    <span className="text-[13px] uppercase tracking-[0.12em] text-graphite/80">{o.fee}</span>
                  </label>
                ))}
                {fieldErrors.delivery && <div role="alert" className="text-sm text-burgundy" data-testid="delivery-error">{fieldErrors.delivery}</div>}
              </div>

              {deliveryMode === "pickup" && pickupStores.length > 0 && (
                <div className="mt-6" data-testid="pickup-stores">
                  <div className="text-[13px] uppercase tracking-[0.12em] text-graphite/70">
                    {t("checkout.pickup.pick_store", "Mağazanı seçin")}
                  </div>
                  {hasOutletItems && (
                    <p className="mt-2 text-sm text-graphite/60" data-testid="pickup-outlet-note">
                      {t("checkout.pickup.outlet_note", "Səbətinizdə outlet məhsulu var — outlet məhsulları yalnız Outlet mağazasından təhvil alınır.")}
                    </p>
                  )}
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {pickupStores.map((store) => (
                      <label
                        key={store.id}
                        data-testid={`pickup-store-${store.id}`}
                        className={`flex min-h-[72px] cursor-pointer items-start gap-3 border px-4 py-3 transition ${pickupStoreId === store.id ? "border-graphite" : "border-graphite/15 hover:border-graphite/40"}`}
                      >
                        <input
                          type="radio"
                          name="pickup_store"
                          checked={pickupStoreId === store.id}
                          onChange={() => setPickupStoreId(store.id)}
                          className="mt-1 h-4 w-4 accent-graphite"
                        />
                        <span>
                          <span className="block text-[13px] font-medium uppercase tracking-[0.1em]">{pickLocale(store.title, locale)}</span>
                          {pickLocale(store.address, locale) && (
                            <span className="mt-1 block text-sm text-graphite/65">{pickLocale(store.address, locale)}</span>
                          )}
                          {pickLocale(store.hours, locale) && (
                            <span className="mt-1 block text-xs text-graphite/55">{pickLocale(store.hours, locale)}</span>
                          )}
                          {store.map_url && (
                            <a href={store.map_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs underline underline-offset-4">
                              {t("stores.map", "Xəritədə bax")}
                            </a>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                  {fieldErrors.pickup_store && (
                    <div role="alert" className="mt-2 text-sm text-burgundy" data-testid="pickup-store-error">{fieldErrors.pickup_store}</div>
                  )}
                </div>
              )}

              <hr className="my-8 border-graphite/10" />

              <h2 className="font-serif text-xl">{t("checkout.delivery.details", "Çatdırılma məlumatları")}</h2>
              <div className="mt-6 grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2" data-testid="checkout-contact">
                <Field label={t("checkout.field.first", "Ad")} testid="ck-first" name="given-name" autoComplete="given-name" placeholder={t("checkout.field.first", "Ad")} value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} error={fieldErrors.first_name} />
                <Field label={t("checkout.field.last", "Soyad")} testid="ck-last" name="family-name" autoComplete="family-name" placeholder={t("checkout.field.last", "Soyad")} value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} error={fieldErrors.last_name} />
                <PhoneField
                  label={t("checkout.field.phone_full", "Mobil nömrə")}
                  value={form.phone}
                  action={
                    <button type="button" onClick={sendOtp} disabled={otpBusy || otpVerified || resendSeconds > 0} className="shrink-0 whitespace-nowrap bg-graphite px-4 text-[13px] font-medium text-white transition hover:bg-espresso disabled:cursor-not-allowed disabled:opacity-50" data-testid="otp-send">
                    {otpVerified
                      ? t("checkout.otp.verified", "Təsdiqləndi")
                      : otpBusy ? "…"
                      : resendSeconds > 0 ? t("checkout.otp.resend_in", "{seconds} san sonra", { seconds: resendSeconds })
                      : otpSent ? t("checkout.otp.resend", "Yenidən göndər")
                      : t("checkout.otp.send", "Kod göndər")}
                    </button>
                  }
                  onChange={(v) => { setForm({ ...form, phone: e164Phone(v) || localPhoneDigits(v) }); setOtpVerified(false); setOtpSent(false); setOtpCode(""); setOtpPhone(""); setResendSeconds(0); }}
                  error={fieldErrors.phone}
                />
                <Field label={t("checkout.field.email_full", "Email ünvanı")} testid="ck-email" name="email" type="email" autoComplete="email" inputMode="email" placeholder="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} error={fieldErrors.email} />
                {otpVerified && (
                  <span className="sm:col-span-2 text-sm text-graphite/70" data-testid="otp-verified">
                    {t("checkout.otp.phone_verified_short", "Telefon təsdiqləndi")}
                  </span>
                )}
                {otpSent && !otpVerified && (
                  <div className="sm:col-span-2 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
                    <Field label={t("checkout.otp.code_label", "6 rəqəmli kod")} placeholder="000000" testid="ck-otp" name="one-time-code" autoComplete="one-time-code" inputMode="numeric" value={otpCode} onChange={setOtpCode} error={fieldErrors.otp} />
                    <button type="button" onClick={confirmOtp} disabled={otpBusy} className="mt-[26px] min-h-[48px] self-start bg-graphite px-6 text-sm font-medium text-white transition hover:bg-espresso disabled:opacity-50" data-testid="otp-confirm">
                      {otpBusy ? "…" : t("checkout.otp.confirm", "Təsdiqlə")}
                    </button>
                    {otpDebugCode && <div className="text-xs text-graphite/70" data-testid="otp-debug">{t("checkout.otp.dev_code", "Test kodu")}: {otpDebugCode}</div>}
                  </div>
                )}
                {otpInfo && <div className="sm:col-span-2 text-sm text-graphite/70" data-testid="otp-info">{otpInfo}</div>}
                {needsAddress && (
                  <>
                    <Field label={t("checkout.field.city", "Şəhər")} testid="ck-city" name="address-level2" autoComplete="address-level2" placeholder="Bakı" value={form.city} onChange={(v) => setForm({ ...form, city: v })} error={fieldErrors.city} full />
                    <Field label={t("checkout.field.address_full", "Ünvan (küçə, bina, blok, mənzil)")} testid="ck-line1" name="address-line1" autoComplete="address-line1" placeholder={t("checkout.field.address_full", "Ünvan (küçə, bina, blok, mənzil)")} value={form.line1} onChange={(v) => setForm({ ...form, line1: v })} error={fieldErrors.line1} full />
                  </>
                )}
              </div>

              <hr className="my-8 border-graphite/10" />

              <h2 className="font-serif text-xl">{t("checkout.gift.title", "Hədiyyəlik məlumatları")}</h2>
              <p className="mt-2 text-[15px] text-graphite/65">
                {t("checkout.gift.body", "Zəhmət olmasa əmin olun ki, çatdırılacaq şəxsin məlumatları dəqiqdir")}
              </p>
              <label className="mt-5 flex min-h-[44px] cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  checked={giftEnabled}
                  onChange={(e) => setGiftEnabled(e.target.checked)}
                  className="h-[18px] w-[18px] accent-graphite"
                  data-testid="ck-gift-toggle"
                />
                <span className="text-[13px] uppercase tracking-[0.12em]">{t("checkout.gift.toggle", "Sifarişi hədiyyə kimi göndər")}</span>
              </label>
              {giftEnabled && (
                <div className="mt-6 grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2" data-testid="checkout-gift-fields">
                  <Field label={t("checkout.gift.first", "Qəbul edən şəxsin adı")} testid="ck-gift-first" placeholder={t("checkout.gift.first", "Qəbul edən şəxsin adı")} value={gift.first_name} onChange={(v) => setGift({ ...gift, first_name: v })} error={fieldErrors.gift_first} />
                  <Field label={t("checkout.gift.last", "Qəbul edən şəxsin soyadı")} testid="ck-gift-last" placeholder={t("checkout.gift.last", "Qəbul edən şəxsin soyadı")} value={gift.last_name} onChange={(v) => setGift({ ...gift, last_name: v })} />
                  <PhoneField
                    className="sm:col-span-2"
                    label={t("checkout.gift.phone", "Qəbul edən şəxsin mobil nömrəsi")}
                    value={gift.phone}
                    testid="ck-gift-phone"
                    onChange={(v) => setGift({ ...gift, phone: e164Phone(v) || localPhoneDigits(v) })}
                    error={fieldErrors.gift_phone}
                  />
                  <label className="block sm:col-span-2">
                    <span className="text-[12px] uppercase tracking-[0.14em] text-graphite">{t("checkout.gift.note_label", "Qeyd")}</span>
                    <textarea
                      value={gift.note}
                      onChange={(e) => setGift({ ...gift, note: e.target.value })}
                      placeholder={t("checkout.gift.note_label", "Qeyd")}
                      rows={4}
                      className="mt-2 w-full resize-y border border-graphite/20 bg-white px-3 py-3 font-sans text-[15px] outline-none transition focus:border-graphite"
                      data-testid="ck-gift-note"
                    />
                  </label>
                </div>
              )}

              <div className="mt-8 space-y-3">
                <label className={`flex min-h-[48px] items-start gap-3 text-sm leading-6 ${fieldErrors.terms_accepted ? "text-burgundy" : "text-graphite/75"}`}>
                  <input type="checkbox" checked={form.terms_accepted} onChange={(e) => setForm({ ...form, terms_accepted: e.target.checked })} className="mt-1 h-4 w-4 accent-graphite" data-testid="ck-terms" />
                  <span>
                    {t("checkout.terms", "İstifadə şərtləri və məxfilik siyasəti ilə razıyam.")}
                    {fieldErrors.terms_accepted && <span className="mt-1 block text-xs" data-testid="terms-error">{fieldErrors.terms_accepted}</span>}
                  </span>
                </label>
                <label className="flex min-h-[40px] items-start gap-3 text-sm leading-6 text-graphite/60">
                  <input type="checkbox" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} className="mt-1 h-4 w-4 accent-graphite" data-testid="ck-marketing" />
                  <span>{t("checkout.marketing", "Atelier Di Falco yeniliklərini və şəxsi təklifləri almaq istəyirəm.")}</span>
                </label>
              </div>

              {fieldErrors.otp && !otpSent && <div role="alert" className="mt-4 text-sm text-burgundy" data-testid="otp-error">{fieldErrors.otp}</div>}
              {err && <div className="mt-4 border border-burgundy/30 bg-burgundy/5 px-4 py-3 text-sm text-burgundy" role="alert" data-testid="checkout-error">{err}</div>}

              <button
                type="button"
                onClick={continueNext}
                className="mt-10 min-h-[56px] w-full bg-graphite text-[15px] font-medium text-white transition hover:bg-espresso"
                data-testid="ck-next"
              >
                {t("checkout.to_payment", "Ödəmə mərhələsinə keç")}
              </button>
              <div className="mt-6 text-center">
                <Link to="/cart" className="text-[13px] uppercase tracking-[0.14em] underline underline-offset-8 hover:text-espresso" data-testid="ck-back-to-cart">
                  {t("checkout.back_to_cart", "Səbətə qayıt")}
                </Link>
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="checkout-payment">
              <h2 className="font-serif text-xl">{t("checkout.review.title", "Sifarişi nəzərdən keçirin")}</h2>
              <div data-testid="checkout-review" className="mt-6 grid gap-6 sm:grid-cols-2">
                <ReviewBlock title={t("checkout.review.contact", "Əlaqə")} testid="review-contact" lines={[`${form.first_name} ${form.last_name}`, form.phone, form.email]} />
                <ReviewBlock
                  title={t("checkout.review.delivery", "Çatdırılma")}
                  testid="review-delivery"
                  lines={[
                    selectedDelivery ? `${selectedDelivery.name} · ${selectedDelivery.fee}` : form.delivery,
                    ...(needsAddress
                      ? [form.line1, form.line2, form.city].filter(Boolean)
                      : [pickupStore && pickLocale(pickupStore.title, locale), pickupStore && pickLocale(pickupStore.address, locale)].filter(Boolean)),
                  ]}
                />
                {giftEnabled && (
                  <ReviewBlock
                    title={t("checkout.review.gift", "Hədiyyə")}
                    testid="review-gift"
                    lines={[`${gift.first_name} ${gift.last_name}`.trim(), gift.phone, gift.note]}
                  />
                )}
              </div>

              <hr className="my-8 border-graphite/10" />

              <h2 className="font-serif text-xl">{t("checkout.payment.pick", "Ödəniş üsulunu seçin")}</h2>
              <div className="mt-5 space-y-3">
                {availableProviders.map((p) => (
                  <label key={p.code} data-testid={`pay-${p.code}`} className={`flex min-h-[64px] cursor-pointer items-center gap-4 border px-4 py-3 transition ${form.payment_provider === p.code ? "border-graphite" : "border-graphite/15 hover:border-graphite/40"}`}>
                    <input type="radio" name="pay" checked={form.payment_provider === p.code} onChange={() => setForm({ ...form, payment_provider: p.code })} className="h-4 w-4 accent-graphite" />
                    <span>
                      <span className="block text-[15px] font-medium">{p.display_name}</span>
                      <span className="mt-1 block text-sm text-graphite/60">
                        {p.code === "cod" && t("checkout.payment.cod", "Çatdırılma zamanı atelier kuryerinə nağd ödəyin.")}
                        {p.code === "bank_transfer" && t("checkout.payment.bank_transfer", "Sifariş verildikdən sonra atelier-in bank rekvizitlərini alacaqsınız.")}
                        {p.code === "kapitalbank" && t("checkout.payment.kapitalbank", "Təhlükəsiz bank ödəniş səhifəsinə yönləndiriləcəksiniz.")}
                        {p.code === "stripe" && t("checkout.payment.stripe", "Təhlükəsiz Stripe ödəniş səhifəsinə yönləndiriləcəksiniz.")}
                      </span>
                    </span>
                  </label>
                ))}
                {fieldErrors.payment_provider && <div role="alert" className="text-sm text-burgundy" data-testid="payment-error">{fieldErrors.payment_provider}</div>}
              </div>

              {redeemMax > 0 && (
                <div className="mt-6 border border-graphite/15 px-4 py-4" data-testid="checkout-redeem">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="text-[13px] uppercase tracking-[0.12em]">
                      {t("checkout.redeem.title", "Falcon Points")}
                    </span>
                    <span className="text-sm text-graphite/70">
                      {t("checkout.redeem.balance", "Balans")}: {loyalty.balance} · {formatAZN(redeemValue(loyalty.balance))}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={redeemMax}
                    step={POINTS_PER_AZN}
                    value={redeemApplied}
                    onChange={(e) => setRedeem(Number(e.target.value))}
                    aria-label={t("checkout.redeem.title", "Falcon Points")}
                    data-testid="ck-redeem-range"
                    className="mt-4 w-full accent-graphite"
                  />
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-sm">
                    <span data-testid="ck-redeem-value">
                      {redeemApplied > 0
                        ? `${redeemApplied} ${t("checkout.redeem.points", "xal")} · − ${formatAZN(redeemValue(redeemApplied))}`
                        : t("checkout.redeem.none", "Xal istifadə edilmir")}
                    </span>
                    <button
                      type="button"
                      onClick={() => setRedeem(redeemApplied === redeemMax ? 0 : redeemMax)}
                      className="underline underline-offset-4 hover:text-graphite"
                      data-testid="ck-redeem-max"
                    >
                      {redeemApplied === redeemMax
                        ? t("checkout.redeem.clear", "Ləğv et")
                        : t("checkout.redeem.max", "Maksimum istifadə et")}
                    </button>
                  </div>
                  <p className="mt-3 text-xs leading-5 text-graphite/60">
                    {t("checkout.redeem.note", "Sifarişin ən çox yarısını xal ilə ödəmək olar.")}
                  </p>
                </div>
              )}

              <div className="mt-6">
                <Field label={t("checkout.field.notes", "Atelier üçün qeyd")} testid="ck-notes" placeholder={t("checkout.field.notes", "Atelier üçün qeyd")} value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} full />
              </div>

              {err && <div className="mt-4 border border-burgundy/30 bg-burgundy/5 px-4 py-3 text-sm text-burgundy" role="alert" data-testid="checkout-error">{err}</div>}

              <button
                type="button"
                onClick={submit}
                disabled={busy}
                className="mt-10 min-h-[56px] w-full bg-graphite text-[15px] font-medium text-white transition hover:bg-espresso disabled:opacity-50"
                data-testid="ck-place-order"
              >
                {busy ? "…" : `${["stripe", "kapitalbank"].includes(form.payment_provider) ? t("checkout.pay_securely", "Təhlükəsiz ödənişə davam et") : t("checkout.place_order", "Sifarişi təsdiqlə")} — ${formatAZN(payableTotal)}`}
              </button>
              <div className="mt-6 text-center">
                <button type="button" onClick={() => { setStep(0); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="text-[13px] uppercase tracking-[0.14em] underline underline-offset-8 hover:text-espresso" data-testid="ck-back">
                  {t("checkout.back", "Geri")}
                </button>
              </div>
            </div>
          )}
        </div>

        <aside className="order-1 lg:order-2">
          {/* The reference summary: a quiet card on its own ground, the order's
              pieces above the arithmetic. */}
          <div className="bg-paper p-6 sm:p-7 lg:sticky lg:top-28" data-testid="checkout-summary">
            <h2 className="font-serif text-xl">{t("checkout.summary.title", "Sifarişin yekunu")}</h2>
            <hr className="mt-4 border-graphite/10" />
            <div className="mt-5 max-h-[280px] space-y-5 overflow-auto pr-1 lg:max-h-none">
              {(cart.items || []).map((it, idx) => (
                <div key={it.id} className="flex gap-4">
                  <div className="h-[84px] w-[64px] shrink-0 overflow-hidden bg-white">
                    <AdfImage
                      source={it.thumbnail_url || productImage(idx)}
                      sizes="64px"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[15px] font-medium leading-tight">{pickLocale(it.product_title)}</div>
                    <div className="mt-1 text-[12px] uppercase tracking-[0.08em] text-graphite/70">
                      {/* No "Ölçü" prefix: most variants here are colours or
                          gift-card amounts, and calling them a size was wrong. */}
                      {it.variant_title}
                      <br />{t("cart.item.qty", "Say")}: {it.quantity}
                    </div>
                    <div className="mt-2 text-sm font-medium">{formatAZN(it.total)}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 space-y-3 border-t border-graphite/10 pt-5">
              <SummaryRow testid="checkout-subtotal" label={t("checkout.subtotal_caps", "Cəmi")} value={formatAZN(cart.subtotal)} />
              {((cart.discount_total || 0) - (cart.tier_discount_minor || 0)) > 0 && (
                <SummaryRow testid="checkout-discount" label={t("checkout.discount", "Endirim")} value={`− ${formatAZN((cart.discount_total || 0) - (cart.tier_discount_minor || 0))}`} />
              )}
              {(cart.tier_discount_minor || 0) > 0 && (
                <SummaryRow testid="checkout-tier-discount" label={t("cart.tier_discount", "Falcon üzvlük endirimi")} value={`− ${formatAZN(cart.tier_discount_minor)}`} />
              )}
              {(cart.gift_card_total || 0) > 0 && (
                <SummaryRow testid="checkout-gift-card" label={t("checkout.gift_card", "Hədiyyə kartı")} value={`− ${formatAZN(cart.gift_card_total)}`} />
              )}
              {redeemApplied > 0 && (
                <SummaryRow
                  testid="checkout-points"
                  label={`${t("checkout.points_line", "Falcon xalları")} (${redeemApplied} ${t("checkout.redeem.points", "xal")})`}
                  value={`− ${formatAZN(redeemValue(redeemApplied))}`}
                />
              )}
              <SummaryRow
                testid="checkout-shipping"
                label={t("checkout.shipping", "Çatdırılma")}
                value={(cart.shipping_total || 0) > 0
                  ? formatAZN(cart.shipping_total)
                  : t("checkout.delivery.free", "Ödənişsiz")}
              />
            </div>
            <div className="mt-5 flex items-baseline justify-between border-t border-graphite/10 pt-5">
              <span className="text-[13px] uppercase tracking-[0.14em]">{t("checkout.total_caps", "Yekun")}</span>
              <span className="text-lg font-semibold tracking-wide" data-testid="checkout-total">
                {formatAZN(payableTotal)}
              </span>
            </div>
            {(cart.points_to_earn || 0) > 0 && (
              <div className="mt-3 flex items-center gap-2 border border-espresso/25 bg-espresso/5 px-3 py-2 text-[13px] text-espresso" data-testid="checkout-points-earn">
                <span aria-hidden="true">✦</span>
                <span>{t("cart.points_earn", "Bu sifarişlə {n} Falcon xalı qazanacaqsınız.", { n: cart.points_to_earn })}</span>
              </div>
            )}
          </div>
        </aside>
      </section>
      </main>
      <Footer />
    </div>
  );
}

function SummaryRow({ label, value, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-4" data-testid={testid}>
      <span className="text-[13px] uppercase tracking-[0.14em] text-graphite/80">{label}</span>
      <span className="text-[13px] uppercase tracking-[0.08em]">{value}</span>
    </div>
  );
}

/**
 * The same phone control the sign-in screen uses; the value leaves in E.164.
 */
function PhoneField({ label, value, onChange, error, action, className = "", testid = "ck-phone" }) {
  const digits = localPhoneDigits(value);
  const errorId = error ? `${testid}-error` : undefined;
  return (
    <label className={`block ${className}`}>
      <span className="text-[12px] uppercase tracking-[0.14em] text-graphite">{label}</span>
      {/* The control and its button are flex items of one row, so they share a
          height and a top edge by construction. */}
      <div className="mt-2 flex items-stretch gap-2">
      <div className={`min-w-0 flex-1 adf-auth-phone-control ${isCompletePhone(value) ? "is-valid" : ""} ${error ? "has-error" : ""}`}>
        <div className="adf-auth-country" aria-hidden="true">
          <span>🇦🇿</span>
          <strong>{AZ_PREFIX}</strong>
        </div>
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          name="tel"
          data-testid={testid}
          value={formatLocalPhone(digits)}
          onChange={(event) => onChange(event.target.value)}
          onPaste={(event) => {
            event.preventDefault();
            onChange(event.clipboardData.getData("text"));
          }}
          placeholder="(50) 123 45 67"
          aria-invalid={error ? "true" : undefined}
          aria-describedby={errorId}
          className="w-full bg-transparent px-3 font-sans text-[15px] outline-none"
        />
      </div>
      {action}
      </div>
      {error && <span id={errorId} role="alert" className="mt-1 block text-xs text-burgundy">{error}</span>}
    </label>
  );
}

function Field({ label, value, onChange, full, testid, error, name, type = "text", autoComplete, inputMode, placeholder }) {
  const errorId = error ? `${testid}-error` : undefined;
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-[12px] uppercase tracking-[0.14em] text-graphite">{label}</span>
      <input value={value || ""} data-testid={testid} onChange={(e) => onChange(e.target.value)}
             name={name} type={type} autoComplete={autoComplete} inputMode={inputMode} placeholder={placeholder}
             aria-invalid={error ? "true" : undefined} aria-describedby={errorId}
             className={`mt-2 h-12 w-full border bg-white px-3 font-sans text-[15px] outline-none transition placeholder:text-graphite/35 ${error ? "border-burgundy" : "border-graphite/20 focus:border-graphite"}`} />
      {error && <span id={errorId} role="alert" className="mt-1 block text-xs text-burgundy">{error}</span>}
    </label>
  );
}

function ReviewBlock({ title, lines, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[12px] uppercase tracking-[0.14em] text-graphite/60">{title}</div>
      <div className="mt-2 text-[15px] font-light leading-6 text-graphite/85">
        {lines.filter(Boolean).map((line) => (
          <div key={`${testid}-${line}`}>{line}</div>
        ))}
      </div>
    </div>
  );
}
